import React, { Dispatch, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from 'react';
import classes from './MediaBlock.module.scss';
import validatorClasses from '../validators/helpers/Validator.module.scss';
import classNames from 'classnames';
import { Tooltip } from '../../../components/actions/Tooltip';
import { CSSTransition } from 'react-transition-group';
import { useDidMount } from '../../../hooks/useDidMount';
import { Button } from '../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { IconsEnum } from '../../../constants/icons';
import { PostEditorContext } from '../states/PostEditorProvider';
import { PostEditorGroupDataContext, PostEditorGroupIndexProps } from '../states/PostEditorGroupDataProvider';
import { ActiveCustomizeIndexContext, ActiveCustomizeIndexType } from '../states/ActiveCustomizeIndexProvider';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import { BreakpointsEnum } from '../../../constants/breakpoints';
import useWhatInput from 'react-use-what-input';
import { MediaTypePostEditorState } from '../../../models/post-editor';
import { PostEditorErrorKeys } from '../../../models/validator-props';
import { MediaLibrary } from '../../Publish/MediaLibrary';
import { RadioButtons } from '../../../components/forms/RadioButtons';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import { RadioButtonsType, RadioButtonsViewProps } from '../../../components/forms/RadioButtons/RadioButtons.view';
import { PostMultiMediaAttachMode } from '../../../models/graphql';
import { PostEditorValidationMessages } from '../constants/validation-messages';
import { FormElementAction, FormElementValueType } from '../../../models/basic-objects';
import { MediaItemDraggable } from '../MediaItemDraggable';
import { MediaUploadStatus, VideoLengthLimits } from '../../../constants/media';
import { MediaDraggedLayer } from '../MediaDraggedLayer';
import { hasSeverity } from '../validators/helpers/severity-checker';
import { ErrorMessageSeverity } from '../../../constants/form';
import { getErrorMessages } from '../validators/helpers/getErrorMessages';
import { showToast } from '../../../helpers/showToast';
import { formatErrorMessages } from '../validators/helpers/formatErrorMessages';
import { getCustomizationProfileId } from '../validators/helpers/customization-getter';
import { BeaconContext } from 'providers/BeaconProvider';
import { idGenerator } from 'helpers/idGenerator';
import scssVars from '../../../assets/scss/exportedVars.module.scss';
import { POST_EDITOR_ERROR_CLASS } from '../../../constants/styleConstants';

export type MediaBlockViewProps = {
  //
  onRemoveMedia: (
    media: MediaTypePostEditorState,
    postGroupIndex: PostEditorGroupIndexProps['postGroupIndex'],
    customizeIndex: ActiveCustomizeIndexType
  ) => void;
  showSelectionModal: boolean;
  setShowSelectionModal: Dispatch<SetStateAction<boolean>>;
  formInstance: UseFormReturn;
};

const errorsLists: PostEditorErrorKeys[] = [
  'facebookContentRequired',
  'facebookMediaLimit',
  'finishUpload',
  'googleMediaLimit',
  'googleMinMedia',
  'instagramMediaLimit',
  'linkedinMediaLimit',
  'mediaRequired',
  'noGifsAllowed',
  'noImagesAllowed',
  'noMixedMedia',
  'noVideosAllowed',
  'pinterestMediaLimit',
  'preventAudioOnlyFiles',
  'tikTokMediaLimit',
  'tikTokVideoLength',
  'tikTokVideoSize',
  'twitterMediaLimit',
  'videoNotTranscoded'
];

const warningList: PostEditorErrorKeys[] = [
  'googleMinMedia',
  'noGifsAllowed',
  'noImagesAllowed',
  'noVideosAllowed',
  'preventAudioOnlyFiles',
  'tikTokVideoLength',
  'tikTokVideoSize'
];

export type ReorderMediaIndexes = { dragIndex: number; hoverIndex: number };
export type OnMediaDragHandler = (indexes: ReorderMediaIndexes) => void;
export type VideoLimitsTupleType = [key: keyof typeof VideoLengthLimits, value: number];

export const MediaBlockView = (props: MediaBlockViewProps): JSX.Element => {
  const { showSelectionModal, setShowSelectionModal, onRemoveMedia, formInstance } = props;
  const intentType = useWhatInput('intent');
  const { beacon } = useContext(BeaconContext);
  const [mount, setMount] = useState<boolean>(false);
  const cssRef = useRef<HTMLDivElement>(null);
  const breakpoint = useBreakpoints();
  const isSmall = breakpoint === BreakpointsEnum.small;

  const { state, dispatch } = useContext(PostEditorContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const postGroup = state.postGroups[postGroupIndex];
  const { customizeContentPerProfile, posts, profiles: selectedProfiles } = postGroup;

  const radioRules: RegisterOptions = {
    required: PostEditorValidationMessages.media.varyMedia.required
  };

  const radioDisplay: RadioButtonsViewProps['displayType'] = useMemo(() => {
    switch (breakpoint) {
      case 'small':
      case 'tabletportrait':
      case 'large':
        return 'block';
      default:
        return 'inline';
    }
  }, [breakpoint]);

  const isCustomByProfile = customizeContentPerProfile && typeof customizeIndex === 'number';

  const profiles = isCustomByProfile
    ? selectedProfiles.filter(p => p.profileId === postGroup.posts[customizeIndex].customizedProfileId)
    : selectedProfiles;

  const media = isCustomByProfile ? posts[customizeIndex].content.media : postGroup.groupPostAttributes.content.media;
  const videos = media.filter(m => m.type === 'VIDEO');

  const publishNextIndex = isCustomByProfile
    ? posts[customizeIndex].content.variationMeta.mediaVariationIndex
    : postGroup.groupPostAttributes.content.variationMeta.mediaVariationIndex;

  const canDrag = media.reduce((prev, current) => {
    if (!prev) return prev;
    return current.status === MediaUploadStatus.COMPLETED;
  }, true);

  const varyMediaValue = isCustomByProfile
    ? posts[customizeIndex].content.varyMedia
    : postGroup.groupPostAttributes.content.varyMedia;

  const varyMediaTypeName =
    typeof customizeIndex === 'number'
      ? `postGroup.${postGroupIndex}.customize.${customizeIndex}.media.varyMediaType`
      : `postGroup.${postGroupIndex}.media.varyMediaType`;
  const customizedProfileId = getCustomizationProfileId(postGroup, customizeIndex);
  // const profileId = isCustomByProfile ? posts[customizeIndex].customizedProfileId : null;
  // const isPostDraft = isCustomByProfile
  //   ? posts[customizeIndex].postStatus === PostStatus.DRAFT
  //   : postGroup.groupPostAttributes.postStatus === PostStatus.DRAFT;
  const { errors } = postGroup;
  const hasError = hasSeverity(errors, ErrorMessageSeverity.error, customizedProfileId, undefined, errorsLists);
  // const warningErrors: PostEditorErrorKeys[] = isPostDraft ? errorsLists : warningList;
  const hasWarning =
    !hasError && hasSeverity(errors, ErrorMessageSeverity.warning, customizedProfileId, null, warningList);

  const warningMessages = getErrorMessages(errors, customizedProfileId, null, warningList);
  const errorMessages = getErrorMessages(errors, customizedProfileId, undefined, errorsLists);

  const isPostMultiMediaAttachModeGuard = (value: FormElementValueType): value is PostMultiMediaAttachMode => {
    if (typeof value !== 'string') throw new Error('value is not of type string');
    return value in PostMultiMediaAttachMode;
  };

  const varyMediaAction: FormElementAction = (value, complete) => {
    if (!isPostMultiMediaAttachModeGuard(value)) throw new Error('Value is not a proper PostMultiMediaAttachMode enum');
    complete();
    dispatch({
      type: 'mediaSetVary',
      payload: {
        postGroupIndex,
        varyMedia: value,
        customizeIndex
      }
    });
  };

  const onDragHandler: OnMediaDragHandler = reorderIndexes => {
    dispatch({
      type: 'mediaReorder',
      payload: {
        postGroupIndex,
        reorderIndexes,
        customizeIndex
      }
    });
  };

  const mediaItemsOptions = useMemo<RadioButtonsType[]>(
    () => [
      {
        value: PostMultiMediaAttachMode.MULTIPLE,
        label: 'Publish All Images/Videos In One Post'
      },
      {
        value: PostMultiMediaAttachMode.ALTERNATE,
        label: 'One image/video per cycle'
      }
    ],
    []
  );

  useDidMount(() => {
    setMount(true);
    return () => {
      // set mount to false makes a fade out animation on unmount, and unregister on form instance is needed to make sure we reset the values for varyMedia to default, since if we decide to add media again later on, we would end up having the previous values set as default, since useForm by default keeps values. we need explicitly to say we don't want to keep values (both default (which are undefined always) and current)
      setMount(false);
      formInstance.unregister(varyMediaTypeName, { keepValue: false, keepDefaultValue: false });
    };
  });

  const longestVideoLength = videos.length ? Math.max(...videos.map(v => v.length ?? 0)) : 0;

  const videoLengthProfile: VideoLimitsTupleType | null = longestVideoLength
    ? profiles.reduce((previousValue: VideoLimitsTupleType | null, currentValue) => {
        let key: keyof typeof VideoLengthLimits | null;
        switch (currentValue.__typename) {
          case 'FacebookSocialProfile':
            key = 'facebook';
            break;
          case 'LinkedInSocialProfile':
            key = 'linkedIn';
            break;
          case 'TwitterSocialProfile':
            key = 'twitter';
            break;
          default:
            key = null;
            break;
        }
        if (!key) return previousValue;
        if (!previousValue) return [key, VideoLengthLimits[key]];
        return previousValue[1] < VideoLengthLimits[key] ? previousValue : [key, VideoLengthLimits[key]];
      }, null)
    : null;

  const showVideoLengthWarring = useMemo(() => {
    if (!videoLengthProfile || !longestVideoLength) return false;
    const [_, profileLength] = videoLengthProfile;
    return longestVideoLength > profileLength;
  }, [videoLengthProfile, longestVideoLength]);

  useEffect(() => {
    if (showVideoLengthWarring) {
      showToast({
        severity: 'info',
        summary: 'Video Publishing',
        detail:
          "The video is too long to be published natively. We'll add a link to our self-hosted version of it instead."
      });
    }
  }, [showVideoLengthWarring]);

  const containerClass = classNames(
    isSmall ? classes.MediaItemsWrapperSmall : classes.MediaItemsWrapperNormal,
    'relative'
  );

  const validationClasses = classNames(classes.WrapperMediaBlock, {
    [classes.BorderValidation]: hasError || hasWarning,
    [validatorClasses.Error]: hasError,
    [validatorClasses.Warning]: !hasError && hasWarning,
    [POST_EDITOR_ERROR_CLASS]: hasError
  });
  const tooltipId = useMemo(() => idGenerator('tooltipMedia'), []);
  return (
    <>
      <CSSTransition in={mount} timeout={300} classNames={'fade'} nodeRef={cssRef} unmountOnExit>
        <div className={validationClasses}>
          <div className={classes.MediaBlock} ref={cssRef}>
            <div className={'grid-x align-middle align-justify'}>
              <div className={'cell shrink'}>
                <div className={'grid-x align-middle align-left'}>
                  <div className={classNames(classes.MediaLabel, 'cell', 'shrink')}>Add Media</div>
                  {!(hasWarning || hasError) && (
                    <div className={'cell shrink'}>
                      <Tooltip
                        target={`#tooltipTargetMediaBlock_${
                          customizeIndex !== undefined ? customizeIndex : 'group'
                        }_${postGroupIndex}`}
                        position={'right'}
                        pointer={true}
                        type={'inlined'}
                        autoZIndex={false}
                        autoHide={false}
                        style={{ zIndex: scssVars['z-index-overmodal'] }}>
                        <div>
                          Enhance your post by adding images, videos, or GIFs. Most platforms support multiple media
                          items per post; and you can cycle through different images every time your Post is recycled, keeping your content fresh and engaging.{' '}
                          <a onClick={() => beacon('article', '679', { type: 'modal' })}>Learn more</a>
                        </div>
                      </Tooltip>
                    </div>
                  )}
                  {hasWarning ? (
                    <div className={'cell shrink'}>
                      <Tooltip
                        icon={'warning'}
                        target={`#tooltipTargetMediaBlockWarning_${
                          customizeIndex !== undefined ? customizeIndex : 'group'
                        }_${postGroupIndex}`}
                        position={'right'}
                        pointer={true}
                        type={'inlined'}
                        autoZIndex={false}
                        style={{ zIndex: scssVars['z-index-overmodal'] }}>
                        {formatErrorMessages(warningMessages)}
                      </Tooltip>
                    </div>
                  ) : null}
                  {hasError ? (
                    <div className={'cell shrink'}>
                      <Tooltip
                        icon={'alert'}
                        target={`#tooltipTargetMediaBlockError_${
                          customizeIndex !== undefined ? customizeIndex : 'group'
                        }_${postGroupIndex}`}
                        position={'right'}
                        pointer={true}
                        type={'inlined'}
                        autoZIndex={false}
                        style={{ zIndex: scssVars['z-index-overmodal'] }}>
                        {formatErrorMessages(errorMessages)}
                      </Tooltip>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className={classNames('cell', 'shrink')}>
                <Button
                  type={'a'}
                  variant={'solid'}
                  size={'medium'}
                  color={ButtonColorsEnum.primary}
                  action={() => setShowSelectionModal(true)}
                  icon={{ side: 'left', value: IconsEnum.plus }}>
                  Add Media
                </Button>
              </div>
            </div>
            {media.length ? (
              <div className={containerClass}>
                {media.map((mediaItem, index) => (
                  <MediaItemDraggable
                    mediaItemProps={{
                      onClose: () => onRemoveMedia(mediaItem, postGroupIndex, customizeIndex),
                      type: mediaItem.type,
                      intentType,
                      loading: mediaItem.loading,
                      publishNext: media.length > 1 && publishNextIndex === index + 1,
                      id: mediaItem.id,
                      image: mediaItem.thumbnailUrl || mediaItem.url // fallback to url if thumbnailUrl is not present
                    }}
                    canDrag={canDrag}
                    key={`item-${index}`}
                    index={index}
                    onDragHandler={onDragHandler}
                  />
                ))}
                <MediaDraggedLayer />
              </div>
            ) : null}
            {media.length > 1 ? (
              <>
                <div
                  className={classNames(
                    'margin-top-small-24 margin-bottom-small-24 grid-x',
                    radioDisplay === 'inline' ? 'align-middle' : 'align-top'
                  )}>
                  <div className={'cell shrink'}>
                    <RadioButtons
                      id={`multimediaTooltip_${tooltipId}`}
                      action={varyMediaAction}
                      displayType={radioDisplay}
                      formInstance={formInstance}
                      items={mediaItemsOptions}
                      name={varyMediaTypeName}
                      rules={radioRules}
                      {...(varyMediaValue ? { value: varyMediaValue } : {})}
                      size={'small'}
                    />
                  </div>
                  <div className={classNames(classes.VaryMediaTooltip, ' cell shrink')}>
                    <Tooltip
                      type={'inlined'}
                      autoHide={false}
                      target={`#multimediaTooltip_${tooltipId}`}
                      autoZIndex={false}
                      style={{ zIndex: scssVars['z-index-overmodal'] }}>
                      <>
                        When you add multiple images/videos to a post, you can either decide to publish them all in one
                        go (as a multi-image post), or if you&apos;re recycling the post over multiple cycles, you can
                        vary the media each time it&apos;s recycled. <a onClick={() => beacon('article', '687', { type: 'modal' })}>Learn more</a>.
                      </>
                    </Tooltip>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </CSSTransition>
      {showSelectionModal && <MediaLibrary onCloseHandler={() => setShowSelectionModal(false)} />}
    </>
  );
};
